import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, Loader2, CheckCircle } from "lucide-react";
import axios, { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

const PLATFORMS = [
  { value: "Xbox", label: "Xbox" },
  { value: "PlayStation", label: "PlayStation" },
  { value: "Nintendo", label: "Nintendo" },
  { value: "Steam", label: "Steam" },
  { value: "Epic", label: "Epic Games" },
  { value: "GOG", label: "GOG" },
  { value: "Mobile", label: "Mobile (Android/iOS)" },
  { value: "RetroAchievements", label: "RetroAchievements" },
  { value: "Other", label: "Other" },
];

const SYSTEMS = {
  Xbox: ["Xbox Series X|S", "Xbox One", "Xbox 360", "Xbox (Original)"],
  PlayStation: ["PS5", "PS4", "PS3", "PS2", "PS1", "PSP", "PS Vita"],
  Nintendo: ["Nintendo Switch", "Wii U", "Wii", "Nintendo 3DS", "Nintendo DS", "Game Boy Advance", "Nintendo 64", "GameCube", "SNES", "NES"],
  Steam: ["PC (Windows)", "Linux", "Mac"],
  Epic: ["PC (Windows)"],
  GOG: ["PC (Windows)", "Linux", "Mac"],
  Mobile: ["Android", "iOS"],
  RetroAchievements: [],
  Other: [],
};

export default function GameRequestDialog({ open, onOpenChange, defaultGameName = "" }) {
  const [gameName, setGameName] = useState("");
  const [platform, setPlatform] = useState("");
  const [system, setSystem] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState("");

  // Prefill the game name when opened from a specific game (e.g. a library
  // game that isn't linked to the MDB, or the "Update from Database" dialog).
  useEffect(() => {
    if (open) setGameName((prev) => prev || defaultGameName || "");
  }, [open, defaultGameName]);

  const handleSubmit = async () => {
    if (!gameName.trim() || !platform) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/game-requests`, {
        gameName: gameName.trim(),
        platform,
        system,
        notes: notes.trim(),
      });
      if (data.ok) {
        setSubmitted(true);
        setMessage(data.message || "Request submitted! The admin will review it.");
      } else {
        setMessage(data.error || "Something went wrong");
      }
    } catch (err) {
      setMessage("Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (val) => {
    if (!val) {
      setGameName("");
      setPlatform("");
      setSystem("");
      setNotes("");
      setSubmitted(false);
      setMessage("");
    }
    onOpenChange(val);
  };

  const systems = SYSTEMS[platform] || [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-[#0A0A0A] border-white/10 text-white" data-testid="game-request-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-heading flex items-center gap-2">
            <Send className="w-4 h-4 text-cyan-400" />
            Request a Game
          </DialogTitle>
          <DialogDescription className="text-white/40 text-xs">
            Can't find a game? Let the admin know and it'll be added to the database.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="request-success">
            <CheckCircle className="w-10 h-10 text-green-400" />
            <p className="text-white/70 text-sm text-center">{message}</p>
            <Button
              variant="outline"
              size="sm"
              className="border-white/10 text-white/60"
              onClick={() => handleClose(false)}
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Game Name */}
            <div>
              <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1 block">Game Name</label>
              <Input
                value={gameName}
                onChange={(e) => setGameName(e.target.value)}
                placeholder="e.g. Halo Infinite"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/20"
                data-testid="request-game-name"
                autoFocus
              />
            </div>

            {/* Platform */}
            <div>
              <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1 block">Platform</label>
              <Select value={platform} onValueChange={(v) => { setPlatform(v); setSystem(""); }}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="request-platform-select">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p.value} value={p.value} className="text-white hover:bg-white/10">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* System (optional, shown when platform has systems) */}
            {systems.length > 0 && (
              <div>
                <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1 block">System (optional)</label>
                <Select value={system} onValueChange={setSystem}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="request-system-select">
                    <SelectValue placeholder="Select system" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {systems.map((s) => (
                      <SelectItem key={s} value={s} className="text-white hover:bg-white/10">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1 block">Notes (optional)</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any extra details (e.g. DLC name, release year)"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/20"
                data-testid="request-notes"
              />
            </div>

            {message && <p className="text-red-400 text-xs">{message}</p>}

            {/* Submit */}
            <Button
              className="w-full bg-cyan-600 hover:bg-cyan-500"
              onClick={handleSubmit}
              disabled={!gameName.trim() || !platform || submitting}
              data-testid="request-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Request
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
